import { isEmptyInput, isNullOrUndefined } from '@adaptiva/core/common';
import { LoggingService } from '@adaptiva/core/logging';
import { BaseActionOverlay, BaseOverlay, BeforeSubmitFn, IOverlay, OverlayService } from '@adaptiva/core/overlays';
import { SnackbarService } from '@adaptiva/core/snackbars';
import { UiService } from '@adaptiva/core/ui';
import { EJavaClassNames, SimpleObjectManagerService } from '@adaptiva/data-service';
import { CpeInfo, ECpeInfoPart, ICpeInfo, IEndOfLifeProduct } from '@adaptiva/metadata/common';
import { EFluidInputType } from '@adaptiva/widgets/fluid-input';
import { ETableDisplay, IBuilderConfig, ObjectListBuilderComponent } from '@adaptiva/widgets/object-list-builder';
import { AfterViewInit, ChangeDetectionStrategy, Component, Inject, OnInit, Optional, ViewChild } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

@Component({
	standalone: false,
	changeDetection: ChangeDetectionStrategy.OnPush,
	selector: 'ada-end-of-life-product-editor-action-overlay',
	templateUrl: './end-of-life-product-editor-action-overlay.component.html',
})
export class EndOfLifeProductEditorActionOverlayComponent extends BaseActionOverlay<IEndOfLifeProduct> implements OnInit, AfterViewInit {

	@ViewChild('CpesBuilderInput')
	private _cpeBuilderInput: ObjectListBuilderComponent<ICpeInfo>;

	public cpesBuilderConfig: IBuilderConfig[] = [
		{
			inputKey: 'part',
			inputType: EFluidInputType.INPUT_TYPE_COMBO,
			label: 'Part',
			placeholder: 'Part',
			required: true,
			options: ECpeInfoPart.displayValues(),
			rowDisplayTransformer: ECpeInfoPart.displayString,
			tableDisplay: ETableDisplay.EXPANDABLE_CONTENT,
		},
		{
			inputKey: 'vendor',
			inputType: EFluidInputType.INPUT_TYPE_TEXT,
			label: 'Vendor',
			placeholder: 'Vendor',
			required: true,
			tableDisplay: ETableDisplay.HEADER
		},
		{
			inputKey: 'product',
			inputType: EFluidInputType.INPUT_TYPE_TEXT,
			label: 'Product',
			placeholder: 'Product',
			required: true,
			tableDisplay: ETableDisplay.HEADER
		},
		{
			inputKey: 'version',
			inputType: EFluidInputType.INPUT_TYPE_TEXT,
			label: 'Version',
			placeholder: 'Version',
			tableDisplay: ETableDisplay.EXPANDABLE_CONTENT
		},
		{
			inputKey: 'update',
			inputType: EFluidInputType.INPUT_TYPE_TEXT,
			label: 'Update',
			placeholder: 'Update',
			tableDisplay: ETableDisplay.EXPANDABLE_CONTENT
		},
		{
			inputKey: 'edition',
			inputType: EFluidInputType.INPUT_TYPE_TEXT,
			label: 'Edition',
			placeholder: 'Edition',
			tableDisplay: ETableDisplay.EXPANDABLE_CONTENT
		},
		{
			inputKey: 'language',
			inputType: EFluidInputType.INPUT_TYPE_TEXT,
			label: 'Language',
			placeholder: 'Language',
			tableDisplay: ETableDisplay.EXPANDABLE_CONTENT
		},
		{
			inputKey: 'swEdition',
			inputType: EFluidInputType.INPUT_TYPE_TEXT,
			label: 'Software Edition',
			placeholder: 'Software Edition',
			tableDisplay: ETableDisplay.EXPANDABLE_CONTENT
		},
		{
			inputKey: 'targetSw',
			inputType: EFluidInputType.INPUT_TYPE_TEXT,
			label: 'Target Software',
			placeholder: 'Target Software',
			tableDisplay: ETableDisplay.EXPANDABLE_CONTENT
		},
		{
			inputKey: 'targetHw',
			inputType: EFluidInputType.INPUT_TYPE_TEXT,
			label: 'Target Hardware',
			placeholder: 'Target Hardware',
			tableDisplay: ETableDisplay.EXPANDABLE_CONTENT
		},
		{
			inputKey: 'other',
			inputType: EFluidInputType.INPUT_TYPE_TEXT,
			label: 'Other',
			placeholder: 'Other',
			tableDisplay: ETableDisplay.EXPANDABLE_CONTENT
		}
	];

	public errorMessage: BehaviorSubject<string> = new BehaviorSubject('');

	public readonly softwareProductClassName: EJavaClassNames = EJavaClassNames.SoftwareProduct;

	private _editingMethod: string;

	constructor(
		private _simpleObjectManager: SimpleObjectManagerService,
		private _snackbarService: SnackbarService,
		_logger: LoggingService,
		_overlayService: OverlayService,
		_ui: UiService,
		@Inject(BaseOverlay.token) @Optional() _overlay?: Partial<IOverlay>,
	) {
		super(_overlayService, _ui, _logger, _overlay);
	}

	public override beforeSubmit: BeforeSubmitFn = (): boolean | Observable<boolean> => {
		let _error: string;

		if (isEmptyInput(this.context.name))
			_error = 'Please provide a readable product name';
		else if (isEmptyInput(this.context.reason))
			_error = 'Please provide a reason for removing support';
		else if (isEmptyInput(this.context.cpeMatchStrings))
			_error = 'Please provide at least one CPE Matches';
		else
			_error = '';

		this.errorMessage.next(_error);

		if (_error)
			return false;

		return this._simpleObjectManager.saveObject<IEndOfLifeProduct>(EJavaClassNames.EndOfLifeProduct, this.context).pipe(
			map(() => {
				this._snackbarService.display('Successfully added the unsupported product');
				return true;
			}),
			catchError((e: Error) => {
				_error = this.logger.extractErrorMessage(e);

				this.logger.error(
					'EndOfLifeProductEditorActionOverlayComponent.beforeSubmit',
					'Could not save the unsupported product',
					_error
				)();
				this.overlayService.error(
					'Error saving the new unsupported product',
					_error
				);

				return of(false);
			})
		);
	};

	public ngOnInit(): void {
		if (this.context)
			this.applyContext(this.context);

		this.ready.next(true);
	}

	public ngAfterViewInit(): void {
		const _searchStrings: string[] = this.context?.cpeMatchStrings;
		if (_searchStrings)
			this._cpeBuilderInput.loadCustomObjects(_searchStrings.map((_searchString: string) => CpeInfo.fromString(_searchString, false)));
	}

	public setCpeSearchStrings(_searchInfo: ICpeInfo[]): void {
		this.context.cpeMatchStrings = _searchInfo.map((_info: ICpeInfo) => CpeInfo.toString(_info));
	}

	protected override applyContext(_previousProduct: IEndOfLifeProduct): void {
		this._editingMethod = isNullOrUndefined(this.context?.objectId) ? 'Create' : 'Update';
		this.title = `${this._editingMethod} Unsupported Product`;
		this.confirmText = this._editingMethod;
	}
}
